<script setup>
import { useUsersStore, useMediaStore } from '@/stores'
import { mediaUrl } from '@core/utils/mediaUrl'
import { Cropper, CircleStencil, RectangleStencil } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'

const props = defineProps({
  title: {
    type: String
  },
  mediaIdToCrop: {
    type: String
  },
  isDialogVisible: {
    type: Boolean,
    required: true
  },
  stencil: {
    type: String,
    default: "circle"
  },
  aspectRatio: {
    type: Number,
    default: 1
  }
})
const emit = defineEmits([
  'media-cropped',
])
const usersStore = useUsersStore()
let isEditingMyUser  = null
let myUser = null;
({ myUser, isEditingMyUser } = storeToRefs(usersStore))

const mediaStore = useMediaStore()
let media  = null;
({ media } = storeToRefs(mediaStore))
mediaStore.getMedia()

const setIsVisible = val => {
  emit('update:isDialogVisible', val)
}

const lastCoordinates = ref(null)

const croppingImg = computed(() => {
  return mediaUrl(props.mediaIdToCrop, 'original')
})

const stencilImport = computed(() => {
  if(props.stencil === "circle"){
    return CircleStencil
  } else {
    return RectangleStencil
  }
})

const change = ({ coordinates, canvas }) => {
  lastCoordinates.value = coordinates
}

const requestClose = () => {
  setIsVisible(false)
}

const confirmCrop = async () => {

  if (!props.mediaIdToCrop) return
  await usersStore.showLoadingOverlay()

  try {
    await mediaStore.cropMedia(props.mediaIdToCrop, lastCoordinates.value)
    await mediaStore.getMedia()
    requestClose()
    emit('media-cropped', props.mediaIdToCrop)
  } catch (error) {
    console.error('Upload error:', error)
    // .js-error lives in MediaChooserAndManager's template, not ours — it may
    // not be mounted. Dereferencing null here would abort the catch block and
    // leave the loading overlay stuck on screen forever.
    const errorEl = document.querySelector('.js-error')
    if (errorEl) errorEl.textContent = error
  } finally {
    await usersStore.hideLoadingOverlay()
  }
}
</script>

<template>
  <VDialog
    :model-value="props.isDialogVisible"
    persistent
    fullscreen
    :retain-focus="false"
  >
    <VToolbar
      dark
      color="primary"
      class="toolbar-header"
    >
      <!--      <v-toolbar-items style="width: 58px"></v-toolbar-items> -->
      <VSpacer />
      <VToolbarTitle>{{ title || 'Crop Image' }}</VToolbarTitle>
      <VSpacer />
    </VToolbar>
    <VCard class="pa-0 main-content">
      <Cropper
        class="cropper"
        :src="croppingImg"
        :stencil-component="stencilImport"
        :stencil-props="{
          aspectRatio: aspectRatio
        }"
        @change="change"
      />
      <div class="action-buttons gap-4">
        <VBtn @click="confirmCrop">
          Crop Image
        </VBtn>
        <VBtn
          color="secondary"
          variant="tonal"
          @click="requestClose"
        >
          Don't Crop
        </VBtn>
      </div>
    </VCard>
  </VDialog>
</template>

<style lang="scss" scoped>
.v-toolbar-title{
  flex: unset;
  margin-inline-start:unset;
}

.toolbar-header{
  position: fixed;
  z-index: 1000;
}

.main-content{
  top: 60px;
  position: relative;
  padding-bottom: 80px !important;
}

.action-buttons {
  display: flex;
  position: fixed;
  left: 0;
  bottom: 0;
  width: 100%;
  z-index: 100;
  background: var(--ds-surface-card);
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  border-top: thin solid var(--ds-border-default);
  padding: 1rem;
  box-shadow: 0px -3px 6px rgba(0,0,0,0.025);
}

.cropper {
  height: 100%;
  width: 100%;
  background: #DDD;
}
</style>
